<template>
	<div @dragover="allowDrop" @dragenter="allowDrop" v-loading="loadingWorkflow"
		:style="{ backgroundImage: gridSettingEnabled ? 'none' : backgroundPattern }" id="workflowCanvas">
		<el-alert :closable="false" v-if="(workflow && readOnlyMode) || (workflow && workflow.isActive)"
			class="workflowStateMessage" title="Workflow is in read only mode" type="info" show-icon>
		</el-alert>
		<el-alert :closable="false" v-if="execution && execution.errorOccurred" class="workflowErrorMessage" :title="'An error occurred in this workflow execution' +
			(execution.workflowDeactivatedDueToError
				? ' - Workflow was automatically deactivated due to error as relevant setting was enabled'
				: '')
			" :description="!execution.errorNodeId ? 'Unable to pinpoint integration, error: ' + executionError : ''"
			type="error" show-icon>
		</el-alert>
		<el-alert :closable="false" v-if="!execution && workflow.access === 'read' && readOnlyMode"
			class="workflowWarningMessage" title="You have limited access to this workflow"
			:description="`You have been granted 'View Only' access to this workflow by ${workflow.ownedBy}. You may not edit the workflow in any way`"
			type="warning" show-icon>
		</el-alert>
		<div v-if="processingAnimationInProgress" class="progressWrapper">
			<h4>Workflow execution in progress</h4>
			<div class="progress progress-infinite">
				<div class="progress-bar"></div>
			</div>
			<el-button class="cancelWorkflowAnimation" @click="cancelWorkflowAnimation" type="text">
				Click to exit execution animation
			</el-button>
		</div>
		<cytoscape id="cy" :preConfig="preConfig" :afterCreated="afterCreated" ref="cy"
			:style="{ width: '100%;', height: '100%', opacity: loadingWorkflow ? '0.4' : '1' }" :config="config">
			<cy-element v-for="def in nodes" :key="`${def.data.id}`" :definition="def" />
		</cytoscape>
		<div class="status">
			<i class="el-icon-circle-check"></i>
			<h4 v-if="!execution">Last updated {{ updatedAt }}</h4>
			<h4 v-if="execution">Execution finished {{ executedAt }}</h4>
		</div>
		<nodeSelectionModal @insertContextualNode="insertContextualNode" @nodeDropped="nodeDropped"
			@close="(nodeSelectionMenuVisible = false), (nodeSelectionFilter = null)"
			:visible="nodeSelectionMenuVisible" :nodeSelectionFilter="nodeSelectionFilter"
			@refresh-workflow="$emit('refresh-workflow')" />
		<nodeContentsModal :readOnlyMode="readOnlyMode" ref="nodeContentsModal" @close="contentsModalClosed"
			:visible="nodeContentsVisible" :workflow="workflow" />
		<agentChainOfThought :executionSummary="execution ? true : false" ref="chainOfThought" />
		<div class="controlBar">
			<!-- <el-tooltip content="Auto Arrange Nodes" placement="top" effect="dark">
				<el-button @click="autoArrangeNodes" plain type="primary" style="font-size: 20px"
					icon="el-icon-s-operation" circle></el-button>
			</el-tooltip> -->
			<el-tooltip content="Add Sticky Note" placement="top" effect="dark">
				<el-button id="stickyNotebtn" @click="toggleStickyNotesMETHOD(true);" plain
					:disabled="nodeSelectionMenuVisible || readOnlyMode" type="primary" style="font-size: 20px" circle>
					<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision"
						text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd"
						clip-rule="evenodd" viewBox="0 0 505 512.47" width="20" height="17" fill="currentColor">
						<path fill-rule="nonzero"
							d="M128.88 48.4 46 135.96h82.88V48.4zM80.01 459.85v25.07h397.43V80.18h-24.89v352.27c0 7.55-3.08 14.41-8.04 19.37-5 4.94-11.86 8.03-19.35 8.03H80.01zM452.55 52.62h25.06c6.97 0 13.39 2.67 18.26 7.06l1.09.98c4.96 4.95 8.04 11.8 8.04 19.36v405.05c0 7.55-3.08 14.41-8.04 19.37-4.99 4.94-11.86 8.03-19.35 8.03H79.84c-7.52 0-14.38-3.1-19.34-8.05-4.95-4.96-8.05-11.82-8.05-19.35v-25.22H27.39c-7.52 0-14.38-3.1-19.34-8.05C3.1 446.84 0 439.97 0 432.45V152.57c0-1.14.14-2.25.4-3.31.11-3.22 1.35-6.41 3.74-8.93L130.23 7.12A13.74 13.74 0 0 1 142.29 0h282.87c6.97 0 13.39 2.67 18.26 7.06l1.09.98c4.96 4.95 8.04 11.8 8.04 19.36v25.22zM319.01 136.1c7.65 0 13.85 6.14 13.85 13.71 0 7.57-6.2 13.71-13.85 13.71h-91.49c-7.65 0-13.85-6.14-13.85-13.71 0-7.57 6.2-13.71 13.85-13.71h91.49zm0 92.49c7.65 0 13.85 6.14 13.85 13.71 0 7.57-6.2 13.72-13.85 13.72H133.35c-7.64 0-13.85-6.15-13.85-13.72s6.21-13.71 13.85-13.71h185.66zm0 92.49c7.65 0 13.85 6.14 13.85 13.71 0 7.57-6.2 13.71-13.85 13.71H133.35c-7.64 0-13.85-6.14-13.85-13.71 0-7.57 6.21-13.71 13.85-13.71h185.66zM27.56 163.52V432.3h397.43V27.56H156.44v122.18c0 7.61-6.17 13.78-13.78 13.78H27.56z">
						</path>
					</svg>
				</el-button>
			</el-tooltip>
			<el-tooltip content="Add Integration" placement="top" effect="dark">
				<el-button id="newNodeBtn" @click="nodeSelectionMenuVisible = true"
					:disabled="nodeSelectionMenuVisible || readOnlyMode" type="primary" style="font-size: 20px"
					icon="icon-node" circle></el-button>
			</el-tooltip>
			<el-tooltip content="Toggle Grid" placement="top" effect="dark">
				<el-button id="wf-tour-grid-btn" @click="toggleGrid()" style="font-size: 20px" plain type="primary"
					:icon="gridSettingEnabled ? 'el-icon-s-grid' : 'el-icon-rank'" circle></el-button>
			</el-tooltip>
			<el-tooltip content="Toggle Connector Type" placement="top" effect="dark">
				<el-button id="wf-tour-connector-btn" @click="toggleConnectorType()" style="font-size: 20px; width: 45.09px" plain type="primary"
					class="arrowType" :icon="workflow.settings && workflow.settings.canvasConnectorStyle === 'taxi'
						? 'icon-bezierArrow'
						: 'icon-taxiArrow'
						" circle></el-button>
			</el-tooltip>
			<el-tooltip content="Fit Graph" placement="top" effect="dark">
				<el-button id="wf-tour-fit-btn" @click="fitGraph()" plain type="primary" style="font-size: 20px" icon="el-icon-coordinate"
					circle></el-button>
			</el-tooltip>
			<el-tooltip content="Save Workflow As Image" placement="top" effect="dark">
				<el-button id="wf-tour-img-btn" @click="saveWorkflowAsImg()" plain type="primary" style="font-size: 20px"
					icon="el-icon-picture-outline" circle></el-button>
			</el-tooltip>
			<el-tooltip content="Zoom In" placement="top" effect="dark">
				<el-button id="wf-tour-zoom-in-btn" plain @click="zoom('up')" type="primary" style="font-size: 20px" icon="el-icon-plus"
					circle></el-button>
			</el-tooltip>
			<el-tooltip content="Zoom Out" placement="top" effect="dark">
				<el-button id="wf-tour-zoom-out-btn" plain @click="zoom('down')" type="primary" style="font-size: 20px" icon="el-icon-minus"
					circle></el-button>
			</el-tooltip>
		</div>
	</div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import dblclick from "cytoscape-dblclick";
//import cytoscapeDagre from "cytoscape-dagre";
import nodeContentsModal from "./nodeContentsModal.vue";
import nodeSelectionModal from "./nodeSelectionModal.vue";
import agentChainOfThought from "./agentChainOfThought.vue";
import cyCanvas from "cytoscape-canvas";
import edgehandles from "cytoscape-edgehandles";
import popper from "cytoscape-popper";
import contextMenus from "cytoscape-context-menus";
import automove from "cytoscape-automove";
import gridGuide from "cytoscape-grid-guide";
import "cytoscape-context-menus/cytoscape-context-menus.css";
import copy from "../assets/copyIcon.svg";
import colorIcon from "../assets/colorIcon.svg";
import input from "../assets/node.svg";
import filledInput from "../assets/input.svg";
import paste from "../assets/pasteIcon.svg";
import play from "../assets/playWorkflowIcon.svg";
import nodeLoader from "../assets/nodeLoader.svg";
const arrow =
	"data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4NTYiIGhlaWdodD0iODU2IiB4bWxuczprdj0iaHR0cHM6Ly92ZWN0YS5pby9uYW5vIj48cGF0aCBkPSJNMjE1IDQyOGg0MjdtMCAwTDQ4MS44NzUgNTg4TTY0MiA0MjhMNDgxLjg3NSAyNjgiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBzdHJva2Utd2lkdGg9IjQ1LjY2NyIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==";
import deleteIcon from "../assets/deleteIcon.svg";
import deleteIconPadding from "../assets/deleteIconPadding.svg";
import { timeDifference } from ".././utils/time.js";
import { mapActions, mapGetters } from "vuex";

let interval;
let globalThis;
let sse;
export default {
	props: { workflowObject: Object, readOnlyMode: Boolean, execution: Object },
	components: { nodeSelectionModal, nodeContentsModal, agentChainOfThought },
	computed: {
		...mapGetters(["returnChangesToNodeSettings"]),
		primaryColor() {
			return getComputedStyle(document.documentElement).getPropertyValue("--primaryColor").trim();
		},
		workflow() {
			return this.workflowObject;
		},
		executionError() {
			try {
				const parsedError = JSON.parse(this.execution.error);
				return parsedError.message || parsedError.error;
			} catch {
				return this.execution.error;
			}
		},
	},
	watch: {
		returnChangesToNodeSettings() {
			this.cy.style().update();
		},
		workflow: {
			deep: true,
			immediate: true,
			handler(val) {
				this.isActive = val?.isActive;
			}
		},
	},
	beforeDestroy() {
		clearInterval(interval);
		if (sse) {
			sse.disconnect();
		}
	},
	mounted() {
		this.loadingWorkflow = true;
	},
	methods: {
		...mapActions(["updateUnsavedChangesStateMETHOD",
			"toggleStickyNotesMETHOD",
			"fetchStickyNoteTemplatesMETHOD",
			"insertTemplateIntoWorkflowMETHOD",
			"updateStickyNoteInWorkflowMETHOD",
			"removeStickyNoteFromWorkflowMETHOD"]),
		disableGridAutoSnap(state) {
			if (this.gridSettingEnabled) {
				this.cy.gridGuide({
					snapToGridOnRelease: state,
				});
			}
		},
		// autoArrangeNodes() {
		// 	if (!this.cy) return;

		// 	this.disableGridAutoSnap(false);

		// 	const layout = this.cy.layout({
		// 		name: "dagre",
		// 		rankDir: "LR",
		// 		align: "UL",
		// 		nodeSep: 60,
		// 		rankSep: 180,
		// 		edgeSep: 10,
		// 		animate: true,
		// 		animationDuration: 500,
		// 		fit: true,
		// 		padding: 60,
		// 		ranker: "network-simplex",
		// 		nodeDimensionsIncludeLabels: false,
		// 		ready: () => {
		// 			this.cy.nodes().forEach((node) => {
		// 				if (
		// 					node.data().specialType ||
		// 					node.data().parentNodeId ||
		// 					node.data()?.node?.type === "note"
		// 				) {
		// 					node.lock();
		// 				}
		// 			});
		// 		},
		// 		stop: () => {
		// 			this.cy.nodes().unlock();
		// 		},
		// 	});

		// 	layout.run();

		// 	this.$nextTick(() => {
		// 		this.repositionDecorativeNodes();
		// 		this.cy.nodes().unlock();
		// 		this.disableGridAutoSnap(true);
		// 		this.updateUnsavedChangesStateMETHOD(true);
		// 	});
		// },
		// repositionDecorativeNodes() {
		// 	this.cy.nodes().forEach((node) => {
		// 		const offset = node.data("offset");
		// 		if (!offset) return;

		// 		node.position(offset);
		// 	});
		// },
		cancelWorkflowAnimation() {
			this.processingAnimationInProgress = false;
			this.processingNodeSseEvent = false;
			this.loadingPopper?.destroy();
			this.loElement = null;
			sse?.disconnect();
			this.nodeSseEventQueue = [];
			this.toggleLock(false);
		},
		toggleLock(lock) {
			if (lock) {
				this.cy.userZoomingEnabled(false);
				this.cy.userPanningEnabled(false);
				this.cy.nodes().ungrabify();
			} else {
				this.cy.userZoomingEnabled(true);
				this.cy.userPanningEnabled(true);
				this.cy.nodes().grabify();
			}
		},
		configureNodeInputsOutputs(config) {
			const node = globalThis.cy.nodes(`#${config.nodeId}`);
			node.data().inputs = config.inputs;
			node.data().outputs = config.outputs;
			if (!_.isEqual(node.data().outputs, node.data().node.outputs) && node.data().node.outputs !== undefined) {
				//Outputs have changed
				//Find all connected contextual input nodes and reset there fill status image
				const allSourceConnectionsForNode = node.connectedEdges().filter((each) => {
					return each.data().source === config.nodeId;
				});
				allSourceConnectionsForNode.forEach((each) => {
					const targetNode = globalThis.cy.nodes(`#${each.data().target}`);
					if (targetNode.data().specialType) {
						targetNode.style({
							"background-image": "url(" + input + ")",
						});
					}
				});
				//Outputs have changed to break all connections with node
				globalThis.cy.remove(node.connectedEdges());
				//Reload the form to ensure output changes are reflected in the nodes ability to be tested
				globalThis.$refs.nodeContentsModal.loadFormData(node.data().node, {
					inputs: config.inputs,
					outputs: config.outputs,
					doNotReload: true,
				});
			}
			if (!_.isEqual(node.data().inputs, node.data().node.inputs) && node.data().node.inputs !== undefined) {
				//Inputs have changed
				const existingContextualInputNodes = globalThis.cy.nodes().filter((each) => {
					return each.data().id.includes("_") && each.data().id.split("_")[0] === config.nodeId;
				});
				//Remove all existing contextual input nodes and connected edges
				existingContextualInputNodes.remove();
				//Remove the autoMove rule
				const autoMoveRule = globalThis.autoMoveRules.find((rule) => {
					return rule.parentNodeId === config.nodeId;
				});
				autoMoveRule?.destroy();
				globalThis.autoMoveRules = globalThis.autoMoveRules.filter((rule) => {
					return rule.parentNodeId !== config.nodeId;
				});
				//Add new contextual input nodes
				globalThis.disableGridAutoSnap(false);
				globalThis.addContextualInputsToNode({ id: config.nodeId }, node.renderedPosition());
				globalThis.disableGridAutoSnap(true);
			}
			node.data().node.inputs = node.data().inputs;
			node.data().node.outputs = node.data().outputs;
		},
		async insertContextualNode(node) {
			const contextualInputNode = this.cy.nodes(`#${node.contextualInputNodeId}`);
			const position = {
				x: contextualInputNode.renderedPosition().x,
				y: contextualInputNode.renderedPosition().y + 150,
			};
			this.disableGridAutoSnap(false);
			const newContextualNodeId = await this.addNewNodeToWorkflow(node.integration, position);
			this.disableGridAutoSnap(true);
			if (newContextualNodeId) {
				this.cy.add({
					group: "edges",
					data: {
						source: newContextualNodeId,
						target: node.contextualInputNodeId,
					},
				});
				if (contextualInputNode.data().node.maxConnections) {
					contextualInputNode.style({
						"background-image": "url(" + filledInput + ")",
					});
				}
			}
		},
		highlightNodeError(e) {
			const node = this.cy.nodes(`#${e.nodeId}`);
			node.css({ borderColor: "red" });
			this.cy.fit(`#${e.nodeId}`, 200);
			node.data().deployError = e.error;
		},
		highlightNodeExecutionError() {
			if (this.execution?.error && this.execution.errorNodeId) {
				const errorNode = this.cy.nodes(`#${this.execution.errorNodeId}`);
				errorNode.css({ borderColor: "red" });
				errorNode.data().deployError = this.executionError;
				this.$nextTick(() => {
					this.cy.fit(`#${this.execution.errorNodeId}`, 200);
				});
			}
		},
		highlightExecutionPath() {
			if (this.execution) {
				this.workflow.nodesExecutionList.forEach((nodeId, i) => {
					const node = this.cy.nodes(`#${nodeId}`);
					node.data().executionIndex = i + 1;
				});
				this.cy.nodes().forEach((node) => {
					if (this.workflow.nodesExecutionList.includes(node.data().id)) {
						const outgoingEdges = node.connectedEdges().filter((each) => {
							const isContinueIfNode =
								node.data() && node.data().node && node.data().node.name === "continueIf"
									? true
									: false;
							return (
								(!isContinueIfNode && each.data().source === node.id()) ||
								(isContinueIfNode &&
									each.data().source === node.id() &&
									this.workflow.nodesExecutionList.includes(each.data().target))
							);
						});
						outgoingEdges.forEach((edge) => {
							//Style the edge id to show data was passed via the connection
							edge.css({ lineStyle: "solid", lineColor: "#67C23A", targetArrowColor: "#67C23A" });
						});
					}
				});
			}
		},
		contentsModalClosed() {
			this.nodeContentsVisible = false;
			this.cy.nodes().forEach((node) => {
				node.css({ borderColor: "#A2A2B5" });
			});
		},
		loadWorkflow(noLoading) {
			this.loadingWorkflow = noLoading ? false : true;

			if (this.cy) {
				this.cy.elements().remove();
				this.autoMoveRules.forEach(r => r.destroy?.());
				this.autoMoveRules = [];
			}

			if (this.execution) {
				this.setExecutionDifference();
			} else {
				this.setTimeDifference();
			}
			if (noLoading) {
				return;
			}
			this.gridSettingEnabled = this.workflow.settings.grid || false;
			this.interpretWorkflow();
			this.setConnectorType();
			//Highlight execution error
			this.highlightNodeExecutionError();
			//Highlight execution path
			this.highlightExecutionPath();
			//Grid settings
			this.configureGridGuideOptions(this.cy);
			setTimeout(() => {
				this.loadingWorkflow = false;
			}, 500);
		},
		setConnectorType() {
			this.cy.edges().forEach((edge) => {
				edge.css({
					"curve-style": this.workflow.settings.canvasConnectorStyle || "unbundled-bezier",
				});
				if (edge.target().data().specialType) {
					this.updateEdgeDistances(edge);
				}
			});
		},
		toggleGrid() {
			if (this.workflow.settings && this.workflow.settings.grid) {
				this.gridSettingEnabled = false;
				this.workflow.settings.grid = false;
			} else {
				this.gridSettingEnabled = true;
				this.workflow.settings.grid = true;
			}
			this.configureGridGuideOptions(this.cy);
		},
		toggleConnectorType() {
			if (this.workflow.settings && this.workflow.settings.canvasConnectorStyle === "unbundled-bezier") {
				this.workflow.settings.canvasConnectorStyle = "taxi";
				this.cy.edges().forEach((edge) => {
					edge.css({
						"curve-style": "taxi",
					});
					this.updateEdgeDistances(edge);
				});
			} else {
				this.workflow.settings.canvasConnectorStyle = "unbundled-bezier";
				this.cy.edges().forEach((edge) => {
					edge.css({
						"curve-style": "unbundled-bezier",
					});
					this.updateEdgeDistances(edge);
				});
			}
		},
		zoom(direction) {
			const graph = document.getElementById("cy");
			const height = graph.clientHeight / 2;
			const width = graph.clientWidth / 2;
			const currentZoomLevel = this.cy.zoom();
			if (direction === "up") {
				this.cy.zoom({
					level: currentZoomLevel + 0.2,
					renderedPosition: { x: width, y: height },
				});
			} else if (direction === "down") {
				this.cy.zoom({
					level: currentZoomLevel - 0.2,
					renderedPosition: { x: width, y: height },
				});
			}
		},
		fitGraph() {
			this.cy.fit(100);
			//this.cy.pan({ y: -10 });
		},
		allowDrop(e) {
			if (e.target.tagName === "CANVAS") {
				const rect = e.target.getBoundingClientRect();
				this.dropCoordinates = { x: e.clientX - rect.left, y: e.clientY - rect.top };
				e.preventDefault();
			}
		},
		async nodeDropped(e) {
			this.disableGridAutoSnap(false);
			await this.addNewNodeToWorkflow(e.name, this.dropCoordinates);
			this.disableGridAutoSnap(true);
		},
		async saveWorflowSurfaceChanges(isNewVersion = false, onWorkflowActivationChanged, newVersionName) {
			const nodePositionsAndConnections = this.cy.nodes().map((node) => {
				const outgoingNodes = node
					.connectedEdges()
					.filter((each) => {
						return each.data().source === node.id();
					})
					.map((each) => {
						return each.data().target;
					});
				return {
					nodeId: node.id(),
					canvasPosition: node.renderedPosition(),
					outgoingConnections: outgoingNodes,
				};
			});

			const result = await this.httpHelper("RUNTIME", "PUT", "workflow/updateOne", {
				workflowId: this.workflow.id || this.workflow.workflowId,
				versionId: this.workflow.versionId ?? null,
				versionName: newVersionName || null,
				isNewVersion,
				workflowLastUpdated: this.workflow.updatedAt,
				settings: {
					timezone: this.workflow.settings.timezone,
					workflowMode: this.workflow.settings.workflowMode,
					deactivateOnError: this.workflow.settings.deactivateOnError || false,
					disableExecutionLogs: this.workflow.settings.disableExecutionLogs || false,
					canvasZoomLevel: this.cy.zoom(),
					canvasPanCoordinates: this.cy.pan(),
					canvasConnectorStyle: this.workflow.settings.canvasConnectorStyle || "unbundled-bezier",
					grid: this.workflow.settings.grid,
				},
				nodePositionsAndConnections: nodePositionsAndConnections,
			});
			setTimeout(() => {
				this.$emit("saveWorkflowOperationCompleted");
				if (!result?.error) {
					if (isNewVersion) {
						this.$emit("new-version-created", result?.response?.newVersion?.versionId);
						onWorkflowActivationChanged();
					}
					this.updateUnsavedChangesStateMETHOD(false);
					this.workflow.updatedAt = result.response.workflow.updatedAt;
					this.$notify({
						title: "Success",
						message: isNewVersion ? "Workflow changes saved and a new version has been created" : "Workflow changes saved",
						type: "success",
						position: "bottom-left",
					});
				} else {
					this.$notifyError(this, result.error);
				}
			}, 500);
		},
		async removeNodeFromWorkflow(nodeId) {
			const result = await this.httpHelper("RUNTIME", "DELETE", "workflow/removeNodeFromWorkflow", {
				versionId: this.workflow.versionId ?? null,
				workflowId: this.workflow.id,
				workflowLastUpdated: this.workflow.updatedAt,
				nodeId: nodeId,
			});
			if (!result?.error) {
				this.workflow.updatedAt = result.response.workflowUpdatedAt;
				this.setTimeDifference();
			} else {
				this.$notifyError(this, result.error);
			}
		},
		async addNewNodeToWorkflow(nodeName, position, parameters) {
			const result = await this.httpHelper("RUNTIME", "POST", "workflow/addNewNodeToWorkflow", {
				workflowId: this.workflow.id || this.workflow.workflowId,
				versionId: this.workflow.versionId ?? null,
				workflowLastUpdated: this.workflow.updatedAt,
				name: nodeName,
				canvasPosition: position || { x: 0, y: 0 },
				parameters: parameters || null,
			});
			if (!result?.error) {
				this.workflow.updatedAt = result.response.node.updatedAt;
				this.setTimeDifference();
				this.cy.add({
					group: "nodes",
					data: {
						node: result.response.node,
						id: result.response.node.id,
						inputs: result.response.node.nodeDescription.inputs || [],
						outputs: result.response.node.nodeDescription.outputs || [],
					},
					renderedPosition: position || { x: 0, y: 0 },
				});
				this.addContextualInputsToNode(result.response.node, position);
				
				this.$nextTick(() => {
					this.syncTourAnchors();
				});

				return result.response.node.id;
			} else {
				this.$notifyError(this, result.error);
			}
		},
		async removeNodesFromWorkflow(nodeIds) {
			const result = await this.httpHelper(
				"RUNTIME",
				"DELETE",
				"workflow/removeNodesFromWorkflow",
				{
					versionId: this.workflow.versionId ?? null,
					workflowId: this.workflow.id,
					workflowLastUpdated: this.workflow.updatedAt,
					nodeIds: nodeIds,
				}
			);

			if (!result?.error) {
				this.workflow.updatedAt = result.response.workflowUpdatedAt;
				this.setTimeDifference();
			} else {
				this.$notifyError(this, result.error);
			}
		},
		async addNewNodesToWorkflow(nodes) {
			const result = await this.httpHelper(
				"RUNTIME",
				"POST",
				"workflow/addNewNodesToWorkflow",
				{
					workflowId: this.workflow.id || this.workflow.workflowId,
					versionId: this.workflow.versionId ?? null,
					workflowLastUpdated: this.workflow.updatedAt,
					nodes: nodes,
				}
			);

			if (!result?.error) {
				this.workflow.updatedAt = result.response.workflowUpdatedAt;
				this.setTimeDifference();

				result.response.nodes.forEach((node) => {
					this.cy.add({
						group: "nodes",
						data: {
							node: node,
							id: node.id,
							type: node?.type,
							inputs: node.nodeDescription.inputs || [],
							outputs: node.nodeDescription.outputs || [],
						},
						renderedPosition: node.nodeSettings.canvasPosition || { x: 0, y: 0 },
					});

					this.addContextualInputsToNode(
						node,
						node.nodeSettings.canvasPosition
					);
				});

				return result.response.nodes.map((n) => n.id);
			} else {
				this.$notifyError(this, result.error);
			}
		},
		addContextualInputsToNode({ id }, position) {
			let inputs = this.cy.nodes(`#${id}`).data().inputs;
			if (!inputs?.length) return;
			inputs = inputs.filter((each) => {
				return typeof each === "object";
			});
			const contextualNodeIds = [];
			inputs.forEach((input, index) => {
				const contextualId = id + "_" + input.type;
				contextualNodeIds.push(contextualId);
				this.cy.add({
					group: "nodes",
					data: {
						specialType: "contextualNodeInput",
						node: input,
						id: contextualId,
						parentNodeId: id,
					},
					renderedPosition: {
						x: this.getRelativeContextualPosition(inputs.length, index, position),
						y: position.y + 45 * this.cy.zoom(),
					},
				});
			});
			this.configureAutoMoveGroupRule(contextualNodeIds, id);
		},
		getRelativeContextualPosition(totalInputs, inputPositionIsList, position) {
			const parentWidth = 50;
			const offsetXFactor = 0.45 * this.cy.zoom();
			const totalWidth = (totalInputs - 1) * offsetXFactor * parentWidth;
			const startX = position.x - totalWidth / 2;
			const currentX = startX + inputPositionIsList * offsetXFactor * parentWidth;
			return currentX;
		},
		configureAutoMoveGroupRule(contextualNodeIds, parentNodeId) {
			const group = [
				`#${parentNodeId}`,
				...contextualNodeIds.map((each) => {
					return `#${each}`;
				}),
			];
			const rule = this.cy.automove({
				nodesMatching: group.join(", "),
				reposition: "drag",
				dragWith: group.join(", "),
			});
			rule.parentNodeId = parentNodeId;
			this.autoMoveRules.push(rule);
		},
		setExecutionDifference() {
			this.executedAt = timeDifference(new Date(), new Date(this.execution.finishTime));
		},
		setTimeDifference() {
			clearInterval(interval);
			this.updatedAt = timeDifference(new Date(), new Date(this.workflow.updatedAt));
			interval = setInterval(() => {
				this.updatedAt = timeDifference(new Date(), new Date(this.workflow.updatedAt));
			}, 60000);
		},
		splitByFirstCharacterOccurrence(str, char) {
			const parts = str.split(char);
			return [parts[0], parts.slice(1).join(char)];
		},
		truncateNoteText(text, maxChars = 240) {
			if (!text) return "";

			if (text.length <= maxChars) return text;

			const truncated = text.slice(0, maxChars);

			const lastSpace = truncated.lastIndexOf(" ");
			if (lastSpace > 0) {
				return truncated.slice(0, lastSpace) + "…";
			}

			return truncated + "…";
		},
		getNextNoteColor(current) {
			const idx = this.note_colors.indexOf(current);
			if (idx === -1) return this.note_colors[0];
			return this.note_colors[(idx + 1) % this.note_colors.length];
		},
		interpretWorkflow() {
			const targetNodeExists = (targetId, nodeName, nodeOutputs) => {
				const target = this.workflow.workflowNodes.find((eachNode) => {
					const type = this.splitByFirstCharacterOccurrence(targetId, "_")[1];
					const typeFilter = eachNode.inputs?.find((each) => {
						return each.type === type;
					})?.filter?.nodes;
					let filterPassed = true;
					if (typeFilter && !typeFilter.includes(nodeName)) {
						filterPassed = false;
					}
					return (
						eachNode.id === targetId ||
						(eachNode.id === targetId.split("_")[0] &&
							eachNode.inputs
								?.map((each) => {
									return each.type;
								})
								.includes(type) &&
							filterPassed &&
							nodeOutputs?.length) //We check that outputs exist, otherwise we don't allow connection to contextual input, as node is not associated without outputs
					);
				});
				return target ? true : false;
			};
			this.cy.zoom(this.workflow.settings.canvasZoomLevel || 1);
			if (this.workflow.settings.canvasPanCoordinates) {
				this.cy.pan(this.workflow.settings.canvasPanCoordinates);
			}
			const workflowNodes = this.workflow.workflowNodes;
			const edges = [];
			workflowNodes.forEach((node) => {
				node.outgoingConnections.forEach((edgeId) => {
					if (targetNodeExists(edgeId, node.name, node.outputs)) {
						edges.push({
							group: "edges",
							data: {
								source: node.id,
								target: edgeId,
							},
						});
					}
				});
			});
			this.cy.add(
				workflowNodes.map((node) => {
					if (this.gridSettingEnabled) {
						// Grid guide likes to shift on re render, account for it to preserve view
						const zoom = this.cy.zoom();
						node.nodeSettings.canvasPosition.x = node.nodeSettings.canvasPosition.x - 50 * zoom;
						node.nodeSettings.canvasPosition.y = node.nodeSettings.canvasPosition.y - 50 * zoom;
					}
					return {
						group: "nodes",
						data: {
							node: node,
							id: node.id,
							inputs: node.inputs || node.nodeDescription.inputs || [],
							outputs: node.outputs || node.nodeDescription.outputs || [],
						},
						renderedPosition: node.nodeSettings.canvasPosition,
					};
				})
			);
			this.cy.nodes().forEach((each) => {
				const nodeData = each.data().node;

				if (nodeData?.type === "note") {
					const fullText = nodeData?.data?.text || "";
					const currentColor = nodeData.data?.color || "#FFF7CC";

					each.style({
						label: this.truncateNoteText(fullText, 240),
						"text-wrap": "wrap",
						"text-max-width": "180px",
						"text-valign": "center",
						"text-halign": "center",
						"overflow": "hidden",
						"font-size": "10px",
						"line-height": "1.5px",
						"text-overflow": "ellipsis",
						"background-color": currentColor
					});

					return;
				}

				this.addContextualInputsToNode(nodeData, nodeData.nodeSettings.canvasPosition);
			});
			this.cy.add(edges);
			this.cy.edges().forEach(this.updateEdgeDistances);
			this.cy.nodes().forEach((each) => {
				if (each.data().specialType && each.data().node.maxConnections && each.connectedEdges().length) {
					each.style({
						"background-image": "url(" + filledInput + ")",
					});
				}
			});
		},
		updateEdgeDistances(edge) {
			if (this.workflow.settings.canvasConnectorStyle === "taxi" || edge.target().data().specialType) {
				edge.css({
					"curve-style": "taxi",
				});
			}
			let sourceEndpointX = 0;
			let sourceEndpointY = 0;
			let targetEndpointX = 0;
			let targetEndpointY = 0;
			const { x: sx, y: sy } = edge.source().position();
			const { x: tx, y: ty } = edge.target().position();
			const dx = tx - sx;
			const dy = ty - sy;

			// Decide if the connection is more horizontal or vertical
			if (Math.abs(dx) >= Math.abs(dy)) {
				// It's more horizontal
				if (dx >= 0) {
					sourceEndpointX = 50;
					sourceEndpointY = 0;
					targetEndpointX = -50;
					targetEndpointY = 0;
				} else {
					sourceEndpointX = -50;
					sourceEndpointY = 0;
					targetEndpointX = 50;
					targetEndpointY = 0;
				}
			} else {
				// It's more vertical
				if (dy >= 0) {
					sourceEndpointX = 0;
					sourceEndpointY = 50;
					targetEndpointX = 0;
					targetEndpointY = -50;
				} else {
					sourceEndpointX = 0;
					sourceEndpointY = -50;
					targetEndpointX = 0;
					targetEndpointY = 50;
				}
			}

			edge.data("endpoints-source", [sourceEndpointX + "%", sourceEndpointY + "%"]);
			edge.data("endpoints-target", [targetEndpointX + "%", targetEndpointY + "%"]);

			const fraction = 0.3; // tweak to taste
			let offset;

			if (Math.abs(dx) >= Math.abs(dy)) {
				// Primarily horizontal; use vertical difference as base
				offset = fraction * dy;
				// If target is to the left, invert the offset
				if (dx < 0) {
					offset = -offset;
				}
			} else {
				// Primarily vertical; use horizontal difference as base
				offset = fraction * dx;
				// If target is below, invert the offset
				if (dy > 0) {
					offset = -offset;
				}
			}

			edge.data("distances", [-offset, offset]); //P1, P2
			edge.data("weights", [0.25, 0.75]); //P0, P3
		},
		nodeClicked() {
			return;
		},
		preConfig(cytoscape) {
			if (typeof cytoscape("core", "cyCanvas") !== "function") {
				cyCanvas(cytoscape);
			}
			if (typeof cytoscape("core", "edgehandles") !== "function") {
				edgehandles(cytoscape);
			}
			if (typeof cytoscape("core", "popper") !== "function") {
				popper(cytoscape);
			}
			if (typeof cytoscape("core", "contextMenus") !== "function") {
				contextMenus(cytoscape);
			}
			if (typeof cytoscape("core", "dblclick") !== "function") {
				dblclick(cytoscape);
			}
			if (typeof cytoscape("core", "automove") !== "function") {
				automove(cytoscape);
			}
			if (typeof cytoscape("core", "gridGuide") !== "function") {
				gridGuide(cytoscape);
			}
			// if (typeof cytoscape("core", "dagre") !== "function") {
			// 	cytoscapeDagre(cytoscape);
			// }
		},
		saveWorkflowAsImg() {
			let $this = this;
			const canvas = document.createElement("canvas");
			canvas.id = "tempLayer";
			const ctx = canvas.getContext("2d");
			const mainLayer = this.cy.png();
			const overlayLayer = this.overlayLayer.toDataURL("image/png");
			const imageObj1 = new Image();
			const imageObj2 = new Image();
			imageObj1.src = mainLayer;
			imageObj2.src = overlayLayer;
			imageObj1.onload = function () {
				canvas.width = imageObj1.width;
				canvas.height = imageObj1.height;
				ctx.drawImage(imageObj1, 0, 0, imageObj1.width, imageObj1.height);
				imageObj2.onload = function () {
					ctx.drawImage(imageObj2, 0, 0, imageObj2.width, imageObj2.height);
					let a = document.createElement("a");
					a.href = canvas.toDataURL("image/png");
					a.download = `${$this.workflow.name}.png`;
					a.click();
				};
			};
		},
		async processSseNodeExecutionEvent(nodeSseExecutionEvent) {
			const nodeId = nodeSseExecutionEvent.nodeId;
			const node = this.cy.nodes(`#${nodeId}`);
			let $this = this;
			if (node.length > 0) {
				if (nodeSseExecutionEvent.metaData.preExecutionEvent) {
					this.loadingPopper?.destroy();
					this.wasExecutionCompletionEvent = false;
					this.cy.animate({
						fit: { eles: node, padding: 200 },
						duration: 1000, // duration of pan animation
						easing: "ease-in-out",
						complete: function () {
							let zoomLevel = $this.cy.zoom();
							$this.loElement = document.createElement("div");
							$this.loElement.innerHTML = `<div class="popperIconWrapper"><img class="loaderIcon" src="${nodeLoader}"/></div>`;
							$this.loElement.classList.add("executionOngoingIcon");
							document.querySelector("#workflowCanvas")?.appendChild($this.loElement);
							$this.loadingPopper = node.popper({
								content: $this.loElement,
								popper: {
									placement: "top",
									modifiers: [
										{
											name: "offset",
											options: {
												offset: [0, 6 * zoomLevel],
											},
										},
									],
								},
							});
						},
					});
				} else {
					let twoExecutionCompletionsInRow = false;
					if (this.wasExecutionCompletionEvent) {
						twoExecutionCompletionsInRow = true;
					}
					if (!twoExecutionCompletionsInRow) {
						await new Promise((resolve) => setTimeout(resolve, 3000)); // artificial delay
					}
					this.wasExecutionCompletionEvent = true;
					this.loadingPopper?.destroy();
					this.loElement = null;
					if (nodeSseExecutionEvent.error && nodeSseExecutionEvent.error !== "null") {
						this.highlightNodeError(nodeSseExecutionEvent);
					} else {
						node.css({ borderColor: "green" });
						node.data().node.outputData = nodeSseExecutionEvent.outputData;
						node.data().node.inputData = nodeSseExecutionEvent.inputData;
						node.data().executionRunTimeMs = nodeSseExecutionEvent.metaData.totalRunTimeMs;
						if (twoExecutionCompletionsInRow) {
							// Back to main node with animation
							this.cy.animate({
								fit: { eles: node, padding: 200 },
								duration: 1000,
								easing: "ease-in-out",
							});
							await new Promise((resolve) => setTimeout(resolve, 1000));
						} else {
							this.cy.fit(`#${nodeId}`, 200);
						}
					}
				}
			} else {
				if (nodeSseExecutionEvent.error && nodeSseExecutionEvent.error !== "null") {
					await new Promise((resolve) => setTimeout(resolve, 3000)); // artificial delay
					this.$notify({
						title: "Error",
						message:
							"Workflow terminated due to error. More details can be found in the execution logs. " +
							nodeSseExecutionEvent.error,
						type: "error",
						position: "bottom-left",
						duration: 0,
					});
					this.loadingPopper?.destroy();
					this.loElement = null;
				} else {
					this.$notify({
						title: "Success",
						message: "Workflow execution complete",
						type: "success",
						position: "bottom-left",
					});
				}
			}
		},
		async handleNodeSseEvent(nodeSseExecutionEvent) {
			this.processingAnimationInProgress = true;
			this.nodeSseEventQueue.push(nodeSseExecutionEvent);
			if (!this.processingNodeSseEvent) {
				this.processingNodeSseEvent = true;
				while (this.nodeSseEventQueue.length > 0) {
					const currentEvent = this.nodeSseEventQueue.shift();
					if (currentEvent.metaData?.agentToolSubFlow) {
						if (currentEvent.metaData?.subFlowStart) {
							await new Promise((resolve) => setTimeout(resolve, 1000)); // artificial delay before shifting to the sub flow
						}
						await this.processSseNodeExecutionEvent(currentEvent);
					} else {
						if (currentEvent.metaData?.agentToolExecution) {
							this.chainOfThoughtSseEvents = true;
							await this.$refs.chainOfThought.pushOperationsIntoChainOfThought(currentEvent.metaData);
						} else {
							if (this.chainOfThoughtSseEvents) {
								//Log final output data also
								this.chainOfThoughtSseEvents = false;
								const data = currentEvent.outputData[0]; // Only first data element supported
								await this.$refs.chainOfThought.pushOperationsIntoChainOfThought({
									agent_c_o_t: [
										[
											{
												aiAgentIntegrationId: currentEvent.nodeId,
												finalOutput: data.json?.output || "Final output unavailable",
											},
										],
									],
									dataIndex: 0, // Only first data element supported
								});
								this.$refs.chainOfThought.resetUserClosedView();
							}
							await this.processSseNodeExecutionEvent(currentEvent);
						}
					}
					if (!currentEvent.keepConnectionAlive) {
						this.processingAnimationInProgress = false;
						this.toggleLock(false);
						if (this.sse) {
							this.sse.disconnect();
						}
						break;
					}
				}
				this.processingNodeSseEvent = false;
			}
		},
		async replayWorkflowFromNode({ node }) {
			this.$refs.chainOfThought.reset();
			this.$refs.chainOfThought.resetUserClosedView();
			const nodesExecutionList = this.workflow?.nodesExecutionList || [];
			const workflowNodes = this.workflow?.workflowNodes || [];
			const errorNodeId = this.execution?.errorNodeId;
			const nodeId = node.data().id;
			//Find the index of the node ID in nodesExecutionList
			const startIndex = nodesExecutionList.indexOf(nodeId);
			if (startIndex === -1) {
				return;
			}
			const remainingExecutionIds = nodesExecutionList.slice(startIndex);
			//Filter workflow nodes and preserve order
			const workflowNodesToReplay = remainingExecutionIds
				.map((id) => workflowNodes.find((n) => n.id === id))
				.filter((n) => n !== undefined);
			let mockedSeeEvents = [];
			for (let i = 0; i < workflowNodesToReplay.length; i++) {
				const nodeToReplay = workflowNodesToReplay[i];
				mockedSeeEvents.push({
					error: "null",
					inputData: nodeToReplay.inputData || [],
					keepConnectionAlive: true,
					metaData: { agentToolSubFlow: false, preExecutionEvent: true, subFlowStart: false },
					nodeId: nodeToReplay.id,
					outputData: nodeToReplay.outputData || [],
					workflowId: this.workflow.id,
				});

				const nodeHasToolInput = nodeToReplay.inputs.find((each) => {
					return each.type === "ai_tool";
				});
				const chainOfThought =
					nodeToReplay.inputData?.map((each) => {
						return each.json[`agent_${nodeToReplay.id}`]?.c_o_t || [];
					}) || [];

				if (nodeHasToolInput && chainOfThought.length) {
					// Create versions without outputs
					const chainOfThoughtNoOutput = chainOfThought.map((innerArray) =>
						innerArray.map((item) => {
							const newItem = { ...item };
							delete newItem.toolOutputData;
							delete newItem.toolOutputData_parsed;
							return newItem;
						})
					);

					// Progressive build-up structure for each sub-array
					let accumulatedWithOutput = chainOfThought.map(() => []); // Empty array for each sub-array

					for (let i = 0; i < chainOfThought.length; i++) {
						let tempNoOutput = accumulatedWithOutput.map((innerArray) => [...innerArray]); // Deep copy

						for (let j = 0; j < chainOfThought[i].length; j++) {
							// Add the next object **without output**
							tempNoOutput[i].push(chainOfThoughtNoOutput[i][j]);

							// First event: with new object but **without outputs**
							mockedSeeEvents.push({
								error: "null",
								inputData: null,
								keepConnectionAlive: true,
								metaData: {
									dataIndex: i,
									agent_c_o_t: [_.cloneDeep(tempNoOutput[i])], // Deep copy
									agentToolExecution: true,
								},
								nodeId: null,
								outputData: null,
								workflowId: this.workflow.id,
							});

							// Replace the last added object with its **output version**
							tempNoOutput[i][tempNoOutput[i].length - 1] = chainOfThought[i][j];

							// Second event: same but now with output data
							mockedSeeEvents.push({
								error: "null",
								inputData: null,
								keepConnectionAlive: true,
								metaData: {
									dataIndex: i,
									agent_c_o_t: [_.cloneDeep(tempNoOutput[i])], // Deep copy
									agentToolExecution: true,
								},
								nodeId: null,
								outputData: null,
								workflowId: this.workflow.id,
							});
						}

						// Persist accumulated state for the next iteration
						accumulatedWithOutput = tempNoOutput.map((innerArray) => [...innerArray]);
					}
				}

				mockedSeeEvents.push({
					error: "null",
					inputData: nodeToReplay.inputData || [],
					keepConnectionAlive: true,
					metaData: {
						agentToolSubFlow: false,
						totalRunTimeMs: nodeToReplay.totalRunTimeMs || 0,
					},
					nodeId: nodeToReplay.id,
					outputData: nodeToReplay.outputData || [],
					workflowId: this.workflow.id,
				});
			}
			let errorMockExecution = [];
			if (errorNodeId) {
				const errorNode = workflowNodes.find((each) => {
					return each.id === errorNodeId;
				});
				errorMockExecution = [
					{
						error: "null",
						inputData: errorNode?.inputData || [],
						keepConnectionAlive: true,
						metaData: { agentToolSubFlow: false, preExecutionEvent: true, subFlowStart: false },
						nodeId: errorNodeId,
						outputData: errorNode?.outputData || [],
						workflowId: this.workflow.id,
					},
					{
						error: this.execution.error || "null",
						inputData: errorNode?.inputData || [],
						keepConnectionAlive: true,
						metaData: { agentToolSubFlow: false, totalRunTimeMs: 0 },
						nodeId: errorNodeId,
						outputData: errorNode?.outputData || [],
						workflowId: this.workflow.id,
					},
				];
			}
			mockedSeeEvents = [...mockedSeeEvents, ...errorMockExecution, { keepConnectionAlive: false }];
			for (let i = 0; i < mockedSeeEvents.length; i++) {
				const mockEvent = mockedSeeEvents[i];
				this.handleNodeSseEvent(mockEvent);
			}
		},
		async runWorkflowFromNode({ node }) {
			this.$refs.chainOfThought.reset();
			this.$refs.chainOfThought.resetUserClosedView();
			this.loadingWorkflow = true;
			const nodeId = node.data().id;
			const workflowId = this.workflow.id;
			const isTriggerNode = node.data().node.type === "trigger" || node.data().node.type === "webhook";
			//Trigger nodes don't have input data but they may have output data, they don't get executed in the runtime so their input becomes their output
			const inputData = !isTriggerNode ? node.data().node.inputData : node.data().node.outputData;
			const result = await this.httpHelper("RUNTIME", "POST", "workflow/runWorkflowFromNodeDataPreflight", {
				workflowId: workflowId,
				inputData: inputData,
			});
			if (!result?.error) {
				this.toggleLock(true);
				const authtoken = Vue.$cookies.get("accessToken") ? "Bearer " + Vue.$cookies.get("accessToken") : null;
				//await new Promise((resolve) => setTimeout(resolve, 1000));
				sse = this.$sse.create({
					url: `${process.env.VUE_APP_RUNTIME_ENDPOINT}/workflow/runWorkflowFromNode?workflowId=${workflowId}&nodeId=${nodeId}`,
					format: "json",
					polyfill: true,
					forcePolyfill: true,
					polyfillOptions: {
						headers: {
							Authorization: authtoken,
						},
						heartbeatTimeout: 600000, //10 mins connection
					},
				});
				sse.on("message", async (event) => {
					this.loadingWorkflow = false;
					await this.handleNodeSseEvent(event);
				});
				sse.on("end", () => {
					this.loadingWorkflow = false;
					//Event only sent back when an invalid request happens, such as no preflight request established. Cannot happen in UI.
					if (sse) {
						sse.disconnect();
					}
				});
				sse.connect().catch((err) => {
					this.loadingWorkflow = false;
					this.loadingPopper?.destroy();
					this.loElement = null;
					this.toggleLock(false);
					console.log("Failed to establish trigger workflow from node connection, error: " + err);
					this.$notify({
						title: "Error",
						message: "Workflow execution timed out or failed to initiate",
						type: "error",
						position: "bottom-left",
					});
					if (sse) {
						sse.disconnect();
					}
				});
			} else {
				this.loadingWorkflow = false;
				if (result?.response?.data?.noCredit) {
					this.$notify({
						title: "Warning",
						message: result.response.data.error,
						type: "warning",
						position: "bottom-left",
					});
				} else {
					this.$notify({
						title: "Warning",
						message: "Workflow execution already in progress",
						type: "warning",
						position: "bottom-left",
					});
				}
			}
		},
		configureGridGuideOptions(cy) {
			let grid = false;
			if (this.workflow.settings.grid) {
				grid = true;
			}
			cy.gridGuide({
				snapToGridOnRelease: grid, // Snap to grid on release
				snapToGridDuringDrag: false, // Snap to grid during drag
				snapToAlignmentLocationOnRelease: false, // Snap to alignment location on release
				snapToAlignmentLocationDuringDrag: false, // Snap to alignment location during drag
				distributionGuidelines: false, // Distribution guidelines
				geometricGuideline: false, // Geometric guidelines
				drawGrid: grid, // Draw grid background

				// General
				gridSpacing: 50, // Distance between the lines of the grid.
				snapToGridCenter: true, // Snaps nodes to center of gridlines. When false, snaps to gridlines themselves. Note that either snapToGridOnRelease or snapToGridDuringDrag must be true.

				// Draw Grid
				zoomDash: true, // Determines whether the size of the dashes should change when the drawing is zoomed in and out if grid is drawn.
				panGrid: true, // Determines whether the grid should move then the user moves the graph if grid is drawn.
				gridStackOrder: -1, // Namely z-index
				gridColor: "#dedede", // Color of grid lines
				lineWidth: 1.0, // Width of grid lines

				// Guidelines
				guidelinesStackOrder: 4, // z-index of guidelines
				guidelinesTolerance: 2.0, // Tolerance distance for rendered positions of nodes' interaction.
				guidelinesStyle: {
					strokeStyle: "#1F51FF", // color of geometric guidelines
					geometricGuidelineRange: 400, // range of geometric guidelines
					range: 100, // max range of distribution guidelines
					minDistRange: 15, // min range for distribution guidelines
					distGuidelineOffset: 15, // shift amount of distribution guidelines
					horizontalDistColor: this.primaryColor, // color of horizontal distribution alignment
					verticalDistColor: this.primaryColor, // color of vertical distribution alignment
					lineDash: [1, 2], // line style of geometric guidelines
					horizontalDistLine: [0, 0], // line style of horizontal distribution guidelines
					verticalDistLine: [0, 0], // line style of vertical distribution guidelines
					initPosAlignmentLine: [0, 0], // line style of alignment to initial mouse position
				},
			});
		},
		async enableNoteEditing(node) {
			if (this.readOnlyMode) return;

			const autoResize = () => {
				input.style.height = "auto";

				const maxHeight = 400;
				const newHeight = Math.min(input.scrollHeight + 2, maxHeight);

				input.style.height = newHeight + "px";
				input.style.overflowY = input.scrollHeight > maxHeight ? "auto" : "hidden";

				if (newHeight >= 400) {
					input.style.left = "50%";
					input.style.top = "50%";
					input.style.transform = "translate(-50%, -50%)";
				}
			};

			const oldText = (node.data().node && node.data().node.data.text) || "";

			const overlay = document.querySelector(`[data-note-id="${node.id()}"]`);
			if (overlay) overlay.style.display = "none";

			const container = document.querySelector("#workflowCanvas");
			const existing = container.querySelector(".note-editor");
			if (existing) container.removeChild(existing);

			const input = document.createElement("textarea");
			input.value = oldText;
			input.className = "note-editor";

			container.appendChild(input);
			autoResize();

			const pos = node.renderedPosition();

			const left = Math.round(pos.x) + "px";
			const top = Math.round(pos.y) + "px";

			input.style.position = "absolute";
			input.style.left = left;
			input.style.top = top;
			input.style.transform = "translate(-50%, -50%)";
			input.style.zIndex = 9999;

			input.style.width = "320px";
			input.style.maxWidth = "420px";

			input.style.padding = "10px";
			input.style.resize = "vertical";
			input.style.overflowY = "auto";

			input.style.background = "#ffffff";
			input.style.border = "1px solid #dcdcdc";
			input.style.borderRadius = "6px";
			input.style.boxShadow = "0 8px 24px rgba(0,0,0,0.18)";
			node.style("label", "");

			input.addEventListener("input", autoResize);

			input.focus();
			input.selectionStart = input.selectionEnd = input.value.length;

			const save = async () => {
				if (!container.contains(input)) return;
				const newText = input.value;
				container.removeChild(input);

				const finalText = newText !== oldText ? newText : oldText;

				node.data().node.data.text = finalText;
				node.style("label", this.truncateNoteText(finalText, 240));

				if (newText !== oldText) {
					const result = await this.updateStickyNoteInWorkflowMETHOD({
						workflowId: this.workflow.id,
						versionId: this.workflow.versionId ?? null,
						nodeId: node.id(),
						description: finalText,
						canvasPosition: node.renderedPosition(),
						workflowUpdatedAt: this.workflow.updatedAt
					});

					this.workflow.updatedAt = result.workflowUpdatedAt;
					this.setTimeDifference();
					this.updateUnsavedChangesStateMETHOD(true);
				}
			};

			input.addEventListener("blur", save);
			input.addEventListener("keydown", (ev) => {
				if ((ev.ctrlKey || ev.metaKey) && ev.key === "Enter") {
					input.blur();
				}
				if (ev.key === "Escape") {
					if (container.contains(input)) container.removeChild(input);
				}
			});
		},
		async afterCreated(cy) {
			const $this = this;
			this.cy = await cy;
			globalThis = $this;

			cy.on("zoom", () => {
				const level = cy.zoom();
				console.log(level);
				
				this.$store.commit("SET_CANVAS_ZOOM", level);
			});

			cy.on("click", "node", async (e) => {
				const node = this.cy.nodes(`#${e.target.id()}`);
				if (node.data().node?.type === "note") {
					this.enableNoteEditing(node);
					return;
				}
				if (node.data().specialType) {
					if (node.css()["background-image"].includes("node")) {
						this.nodeSelectionFilter = { ...e.target.data().node, id: e.target.data().id };
						this.nodeSelectionMenuVisible = true;
					}
					return;
				} else {
					this.nodeSelectionFilter = null;
				}
				
				// Re-sync tour anchors when a node is clicked or focused
				this.syncTourAnchors();

				if ($this.execution) {
					node.data().executionRunTimeMs = null;
				}
				if (!$this.readOnlyMode) {
					//Only clear errors and pass data through when not in read only mode
					node.data().deployError = null;
					node.data().executionRunTimeMs = null;

					let partialInputDataList = [];
					if (node.data().outputs?.length) {
						//Associated nodes have outputs that are contextual inputs of the related node whose input data we want
						const outgoingEdges = cy.edges().filter((each) => {
							return each.data().source === e.target.id();
						});
						for (let i = 0; i < outgoingEdges.length; i++) {
							const edge = outgoingEdges[i];
							const targetId = edge.data().target.split("_")[0]; //Target is contextual input, so get only ID of actual integration
							const node = cy.nodes(`#${targetId}`);
							partialInputDataList.push(node.data().node.inputData);
						}
					} else {
						//Regular node with no contextual outputs
						const incomingEdges = cy
							.nodes()
							.connectedEdges()
							.filter((each) => {
								return each.data().target === e.target.id();
							});
						for (let i = 0; i < incomingEdges.length; i++) {
							const edge = incomingEdges[i];
							const sourceId = edge.data().source;
							const node = cy.nodes(`#${sourceId}`);
							partialInputDataList.push(node.data().node.inputData);
							partialInputDataList.push(node.data().node.outputData);
						}
					}

					//Combine all input data sources
					const lengths = partialInputDataList.map((p) => p.length);
					const indexOfLongestPartial = lengths.indexOf(Math.max(...lengths));
					let combinedInputData = partialInputDataList[indexOfLongestPartial] || [];
					for (let i = 0; i < partialInputDataList.length; i++) {
						const inputDataPartial = partialInputDataList[i];
						if (i !== indexOfLongestPartial) {
							combinedInputData = combinedInputData.map((baseDataObject, index) => {
								let binary = {
									...(baseDataObject.binary || {}),
									...(inputDataPartial[index]?.binary || {}),
								};
								let json = {
									...(baseDataObject.json || {}),
									...(inputDataPartial[index]?.json || {}),
								};
								const returnData = {};
								if (Object.keys(binary).length) {
									returnData.binary = binary;
								}
								returnData.json = json;
								return returnData;
							});
						}
					}

					e.target.data().node.inputData = combinedInputData.length
						? combinedInputData
						: [{ json: {}, binary: {} }];
				}
				this.$refs.nodeContentsModal.loadFormData(e.target.data().node, {
					inputs: e.target.data().inputs,
					outputs: e.target.data().outputs,
				});
				this.activeNodeId = e.target.data().node.id;
				this.nodeContentsVisible = true;
				cy.nodes().forEach((node) => {
					node.css({ borderColor: "#A2A2B5" });
				});
				e.target.css({ borderColor: this.primaryColor });
			});

			//Double click logic
			cy.dblclick();
			cy.on("dblclick", (e) => {
				if (!e.target.data().id && !this.readOnlyMode) {
					this.nodeSelectionMenuVisible = true;
				}
			});

			//Cursor logic
			const updateCursorStyle = function (type) {
				document.getElementById("workflowCanvas").style.cursor = type;
			};

			//Run workflow from node logic button starts
			let runWorkflowPopper;
			let runWorkflowElement;
			function setRunButtonOn(node, zoomLevel, isReplay) {
				if (
					node.data().node.outputs?.length ||
					$this.processingNodeSseEvent ||
					$this.processingAnimationInProgress
				) {
					//Running associations not supported and cannot show while execution animation is in progress
					return;
				}
				runWorkflowElement = document.createElement("div");
				runWorkflowElement.innerHTML = `<div class="popperIconWrapper"><img class="popperIcon" src="${play}"/></div>`;
				runWorkflowElement.classList.add("runWorkflowBtn");
				runWorkflowElement.addEventListener("click", () => {
					$this
						.$confirm(
							`${isReplay ? "Replay" : "Run"} the workflow from this integration?`,
							`${isReplay ? "Replay" : "Run"} Workflow`,
							{
								confirmButtonText: `${isReplay ? "Replay" : "Run"} workflow`,
								cancelButtonText: "Cancel",
								type: "warning",
							}
						)
						.then(() => {
							if (isReplay) {
								$this.replayWorkflowFromNode({ node });
							} else {
								$this.runWorkflowFromNode({ node });
							}
							removeRunButton();
						})
						.catch(() => { });
				});
				document.querySelector("#workflowCanvas")?.appendChild(runWorkflowElement);
				runWorkflowPopper = node.popper({
					content: runWorkflowElement,
					popper: {
						placement: "bottom",
						modifiers: [
							{
								name: "offset",
								options: {
									offset: [0, -6 * zoomLevel],
								},
							},
						],
					},
				});
			}

			function removeRunButton() {
				if (runWorkflowPopper) {
					runWorkflowPopper.destroy();
					runWorkflowPopper = null;
				}
				if (runWorkflowElement && document.querySelector(".runWorkflowBtn")) {
					document.querySelector("#workflowCanvas")?.removeChild(runWorkflowElement);
					runWorkflowElement = null;
				}
			}

			//Edge handle logic starts
			const eh = cy.edgehandles();
			let ehNode;
			let ehPopper;
			let ehElement;
			let started = false;
			let lastTarget;
			function setHandleOn(node) {
				if (
					started ||
					!document.querySelector("#workflowCanvas") ||
					$this.readOnlyMode ||
					node.data().specialType ||
					node.data().node?.type === "note"
				) {
					removeRunButton();
					if ($this.execution && $this.workflow?.nodesExecutionList.includes(node.data().id)) {
						let zoomLevel = cy.zoom();
						const isReplay = true;
						setRunButtonOn(node, zoomLevel, isReplay);
					}
					return;
				}
				removeHandle();
				removeRunButton();
				let zoomLevel = cy.zoom();
				if (!$this.workflow.isActive) {
					ehNode = node;
					ehElement = document.createElement("div");
					ehElement.innerHTML = `<div class="popperIconWrapper"><img class="popperIcon" src="${arrow}"/></div>`;
					ehElement.classList.add("edgeHandle");
					ehElement.addEventListener("mousedown", () => {
						eh.start(ehNode);
					});
					document.querySelector("#workflowCanvas")?.appendChild(ehElement);
					ehPopper = node.popper({
						content: ehElement,
						popper: {
							placement: "right",
							modifiers: [
								{
									name: "offset",
									options: {
										offset: [0, -6 * zoomLevel],
									},
								},
							],
						},
					});
				}
				if ($this.workflow.isActive) {
					setRunButtonOn(node, zoomLevel);
				}
			}
			function adjustPopperSize(className) {
				let zoomLevel = cy.zoom();
				let popperElement = document.querySelector(className);
				if (!popperElement) {
					return;
				}
				//Always true if the popper exists in the DOM
				let icon = popperElement.childNodes[0].childNodes[0];
				popperElement.style.height = 12 * zoomLevel + "px";
				popperElement.style.width = 12 * zoomLevel + "px";
				icon.style.width = 12 * zoomLevel + "px";
				icon.style.height = 12 * zoomLevel + "px";
			}
			function removeHandle() {
				if (ehPopper) {
					ehPopper.destroy();
					ehPopper = null;
				}
				if (ehElement && document.querySelector(".edgeHandle")) {
					document.querySelector("#workflowCanvas")?.removeChild(ehElement);
					ehElement = null;
				}
				removeRunButton();
				ehNode = null;
			}
			cy.on("mousedown", "node", (e) => {
				const node = e.target;

				if (node.data().node?.type !== "note") return;

				const box = node.renderedBoundingBox();
				const ev = e.originalEvent;

				if (!ev) return;

				const x = ev.clientX;
				const y = ev.clientY;

				const rect = document
					.getElementById("cy")
					.getBoundingClientRect();

				const rx = x - rect.left;
				const ry = y - rect.top;

				const hitDot =
					rx >= box.x2 - 22 &&
					rx <= box.x2 - 6 &&
					ry >= box.y2 - 22 &&
					ry <= box.y2 - 6;

				if (hitDot) {
					node.data("_noteColorClick", true);
					e.preventDefault();
					e.stopPropagation();
				}
			});
			cy.on("mouseover", "node", function (e) {
				updateCursorStyle("pointer");
				setHandleOn(e.target);
				adjustPopperSize(".edgeHandle");
				adjustPopperSize(".runWorkflowBtn");
				lastTarget = e.target;
			});
			cy.on("mouseout", "node", function () {
				updateCursorStyle("grab");
				//Removes handle when mouse exits a node
				lastTarget = null;
				removeHandle();
			});
			cy.on("grab", "node", function () {
				removeHandle();
				if (lastTarget) {
					setHandleOn(lastTarget);
				}
			});
			cy.on("free", "node", (e) => {
				setHandleOn(e.target);
				adjustPopperSize(".edgeHandle");
				adjustPopperSize(".runWorkflowBtn");
				lastTarget = e.target;
				$this.syncTourAnchors();
			});

			function debounce(func, wait) {
				let timeout;
				return function (...args) {
					clearTimeout(timeout);
					timeout = setTimeout(() => func.apply(this, args), wait);
				};
			}
			const setHandleOnProxy = (lastTargetProxy) => {
				setHandleOn(lastTargetProxy);
				adjustPopperSize(".edgeHandle");
				adjustPopperSize(".runWorkflowBtn");
			};
			const debouncedSetHandleOn = debounce(setHandleOnProxy, 200);

			cy.on("zoom pan resize", function () {
				if (lastTarget) {
					//setHandleOn(lastTarget);
					//adjustPopperSize(".edgeHandle");
					//Remove the handles when zoom or pan for performance reasons
					removeHandle();
					debouncedSetHandleOn(lastTarget);
				}
			});

			window.addEventListener("mouseup", function () {
				eh.stop();
			});
			cy.on("ehstart", function () {
				started = true;
			});
			cy.on("ehstop", function () {
				//Need to deselect all text which gets selected when dragging
				window.getSelection().removeAllRanges();
				started = false;
			});
			cy.on("ehcomplete", function (e, sourceNode, targetNode, addedEdge) {
				if (
					sourceNode.data().node?.type === "note" ||
					targetNode.data().node?.type === "note"
				) {
					cy.remove(addedEdge);
					return;
				}
				const edgesWithSameSourceAndTarget = cy
					.edges()
					.toArray()
					.filter(function (ele) {
						return ele.source().id() === sourceNode.id() && ele.target().id() === targetNode.id();
					});
				//Stop nodes having more than one connection of the same direction with eachother
				if (edgesWithSameSourceAndTarget.length > 1) {
					cy.remove(addedEdge);
				}
				//Stop edges connecting to trigger nodes
				if (targetNode.data().node.nodeDescription?.group.includes("trigger")) {
					cy.remove(addedEdge);
					setTimeout(() => {
						$this.$message({
							message: "Trigger integrations are sources only and cannot be connected to.",
							type: "warning",
							offset: 170,
						});
					}, 0);
				}

				//Contextual inputs can only have certain contextual nodes connect to them
				if (targetNode.data().specialType === "contextualNodeInput") {
					const targetNodeIncomingEdgesCount = targetNode.connectedEdges().length;
					const sourceNodeName = sourceNode.data().node.name;
					const sourceNodeOutputs = sourceNode.data().outputs || [];
					const additionalInputFilters = targetNode.data().node.filter?.nodes || [];
					const maxInputConnectionsForTarget = targetNode.data().node.maxConnections;
					let connectionIsAllowed = sourceNodeOutputs.includes(targetNode.data().node.type);
					if (additionalInputFilters.length && !additionalInputFilters.includes(sourceNodeName)) {
						connectionIsAllowed = false;
					}
					if (!connectionIsAllowed) {
						cy.remove(addedEdge);
						setTimeout(() => {
							$this.$message({
								message: "Integration association is not valid.",
								type: "warning",
								offset: 170,
							});
						}, 0);
					}
					if (maxInputConnectionsForTarget && targetNodeIncomingEdgesCount > maxInputConnectionsForTarget) {
						cy.remove(addedEdge);
						setTimeout(() => {
							$this.$message({
								message: `Max associations of ${maxInputConnectionsForTarget} integrations cannot be exceeded.`,
								type: "warning",
								offset: 170,
							});
						}, 0);
					}
					if (connectionIsAllowed && targetNodeIncomingEdgesCount <= maxInputConnectionsForTarget) {
						targetNode.style({
							"background-image": "url(" + filledInput + ")",
						});
					}
				}

				//Remove connection attempt if integration has outputs and the target integration is not a contextual input
				if (sourceNode.data().outputs?.length && targetNode.data().specialType !== "contextualNodeInput") {
					cy.remove(addedEdge);
					setTimeout(() => {
						$this.$message({
							message: "Integration outgoing connection not allowed.",
							type: "warning",
							offset: 170,
						});
					}, 0);
				}

				//Remove connection attempt if target integration has outputs
				if (targetNode.data().outputs?.length) {
					cy.remove(addedEdge);
					cy.remove(addedEdge);
					setTimeout(() => {
						$this.$message({
							message: "Integration outgoing connection not allowed.",
							type: "warning",
							offset: 170,
						});
					}, 0);
				}

				//Specify that there are unsaved changes to global store
				if (
					!targetNode.data().node.nodeDescription?.group.includes("trigger") ||
					edgesWithSameSourceAndTarget.length <= 1
				) {
					$this.updateUnsavedChangesStateMETHOD(true);
				}
			});
			//Edge handles logic stops

			//Edge logic
			let deleteEdgeBtn;
			let buttonElement;
			function removeDeleteBtn() {
				if (deleteEdgeBtn) {
					deleteEdgeBtn.destroy();
					document.querySelector("#workflowCanvas")?.removeChild(buttonElement);
					deleteEdgeBtn = null;
				}
			}
			cy.on("mouseover", "edge", function (e) {
				if ($this.readOnlyMode || $this.workflow.isActive) {
					return;
				}
				if (document.querySelector("#workflowCanvas") && !$this.readOnlyMode) {
					deleteEdgeBtn = e.target.popper({
						content: () => {
							buttonElement = document.createElement("div");
							buttonElement.addEventListener("click", () => {
								removeDeleteBtn();
								cy.remove(e.target);
								const edgeTarget = cy.nodes(`#${e.target.data().target}`);
								if (edgeTarget.data().specialType) {
									//Change connection img
									edgeTarget.style({
										"background-image": "url(" + input + ")",
									});
								}
								$this.updateUnsavedChangesStateMETHOD(true);
							});
							buttonElement.classList.add("deleteEdge");
							buttonElement.innerHTML = `<div class="popperIconWrapper"><img class="popperIcon" src="${deleteIconPadding}"/></div>`;
							document.querySelector("#workflowCanvas")?.appendChild(buttonElement);
							return buttonElement;
						},
					});
				}
				let updateDeleteBtn = () => {
					if (deleteEdgeBtn) {
						deleteEdgeBtn.update();
						let zoomLevel = cy.zoom();
						let deleteEdgeBtnElement = document.querySelector(".deleteEdge");
						if (deleteEdgeBtnElement) {
							deleteEdgeBtnElement.style.height = 12 * zoomLevel + "px";
							deleteEdgeBtnElement.style.width = 12 * zoomLevel + "px";
							let icon = deleteEdgeBtnElement.childNodes[0].childNodes[0];
							icon.style.height = 12 * zoomLevel + "px";
							icon.style.width = 12 * zoomLevel + "px";
						}
					}
				};
				if (deleteEdgeBtn) {
					updateDeleteBtn();
				}
				e.target.on("position", updateDeleteBtn);
				cy.on("pan zoom resize", updateDeleteBtn);
				e.target.css({
					lineStyle: "solid",
					lineColor: $this.primaryColor,
					targetArrowColor: $this.primaryColor,
				});
			});
			cy.on("mouseout", "edge", function (e) {
				if ($this.readOnlyMode) {
					return;
				}
				removeDeleteBtn();
				e.target.css({
					lineStyle: "dashed",
					lineColor: "#A2A2B5",
					targetArrowColor: "#A2A2B5",
				});
			});
			cy.on("position", (e) => {
				e.target.connectedEdges().forEach(this.updateEdgeDistances);
			});
			cy.on("ehpreviewon", () => {
				cy.edges().forEach(this.updateEdgeDistances);
			});

			$this.lastSelectedNodes = cy.collection();

			cy.on("cxttapstart", "node", (e) => {
				const selected = cy.nodes(":selected").filter(
					n => !n.data().specialType
				);

				if (selected.length > 0) {
					$this.lastSelectedNodes = selected;
				} else {
					$this.lastSelectedNodes = cy.collection(e.target);
				}
			});
			//Edge logic stops

			//Node menus
			const contextMenuSettings = {
				evtType: "cxttap",
				menuItems: [
					{
						id: "change-note-color",
						content: "Change Color",
						image: { src: colorIcon, width: 16, height: 16, x: 6, y: 20 },
						selector: "node",
						onClickFunction: async function (e) {
							const node = e.target;

							if (node.data().node?.type !== "note") return;

							const current = node.data().node.data?.color || "#FFF7CC";
							const next = $this.getNextNoteColor(current);

							node.data().node.data.color = next;
							node.style("background-color", next);

							const result = await $this.updateStickyNoteInWorkflowMETHOD({
								workflowId: $this.workflow.id,
								versionId: $this.workflow.versionId ?? null,
								nodeId: node.id(),
								color: next,
								workflowUpdatedAt: $this.workflow.updatedAt
							});
							$this.workflow.updatedAt = result.workflowUpdatedAt;
							$this.setTimeDifference();

							$this.updateUnsavedChangesStateMETHOD(true);
						}
					},
					{
						id: "Delete-Note",
						content: "Delete Note",
						tooltipText: "Delete Node",
						selector: "node",
						image: { src: deleteIcon, width: 16, height: 16, x: 6, y: 20 },
						show: true,
						onClickFunction: async function (e) {
							const targetId = e.target?.data()?.id;

							const result = await $this.removeStickyNoteFromWorkflowMETHOD({
								workflowId: $this.workflow.id,
								versionId: $this.workflow.versionId ?? null,
								nodeId: targetId,
								workflowUpdatedAt: $this.workflow.updatedAt
							});
							$this.workflow.updatedAt = result.workflowUpdatedAt;
							$this.setTimeDifference();

							cy.remove(e.target);
							$this.updateUnsavedChangesStateMETHOD(true);
						},
						disabled: false,
					},
					{
						id: "Delete",
						content: "Delete Integration",
						tooltipText: "Delete Node",
						selector: "node",
						image: { src: deleteIcon, width: 16, height: 16, x: 6, y: 20 },
						show: true,
						onClickFunction: async function (e) {
							let selected = cy.nodes(":selected").filter(n => !n.data().specialType);

							const targetId = e.target?.data()?.id;

							if (selected.length <= 1 && e.target.isNode() && e.target.selected()) {
								selected = cy.nodes(":selected").union(e.target).filter(n => !n.data().specialType);
							}

							if (selected.length > 1) {
								const nodeIds = [];

								selected.forEach((node) => {
									const nodeId = node.id();
									nodeIds.push(nodeId);

									const contextualInputNodes = cy.nodes().filter((each) => {
										return each.data().parentNodeId === nodeId;
									});

									if (contextualInputNodes.length) {
										const autoMoveRule = $this.autoMoveRules.find((each) => {
											return each.parentNodeId === nodeId;
										});
										autoMoveRule?.destroy();
										cy.remove(contextualInputNodes.connectedEdges());
										cy.remove(contextualInputNodes);
									}

									cy.remove(node.connectedEdges());
									cy.remove(node);
								});

								cy.nodes().unselect();

								await $this.removeNodesFromWorkflow(nodeIds);
								$this.updateUnsavedChangesStateMETHOD(true);
								return;
							}

							lastTarget = null;
							removeHandle();

							if ($this.activeNodeId === targetId) {
								$this.nodeContentsVisible = false;
							}
							const relevantNode = cy.nodes(`#${targetId}`);

							//Check for all contextual inputs that are targets of the deleted Node
							//Change their connection image if appropriate
							const connectedToContextualNodes = relevantNode.outgoers().filter((each) => {
								return each.data().specialType;
							});
							connectedToContextualNodes.forEach((each) => {
								each.style({
									"background-image": "url(" + input + ")",
								});
							});

							cy.remove(relevantNode.connectedEdges());
							cy.remove(e.target);
							const contextualInputNodes = cy.nodes().filter((each) => {
								return each.data().parentNodeId === targetId;
							});
							if (contextualInputNodes.length) {
								const autoMoveRule = $this.autoMoveRules.find((each) => {
									return each.parentNodeId === targetId;
								});
								autoMoveRule?.destroy();
								cy.remove(contextualInputNodes.connectedEdges());
								cy.remove(contextualInputNodes);
							}
							await $this.removeNodeFromWorkflow(targetId);
						},
						disabled: false,
					},
					{
						id: "Copy",
						content: "Copy Integration",
						tooltipText: "Copy Node",
						image: { src: copy, width: 17, height: 17, x: 6, y: 20 },
						selector: "node",
						onClickFunction: function (e) {
							const selected = $this.lastSelectedNodes;

							if (selected && selected.length > 1) {
								const nodeIds = selected.toArray().map(n => n.id());

								const edges = cy.edges().filter(edge =>
									nodeIds.includes(edge.source().id()) &&
									nodeIds.includes(edge.target().id())
								);

								$this.copiedBlock = {
									nodes: selected.toArray().map(n => ({
										data: n.data(),
										position: n.renderedPosition()
									})),
									edges: edges.map(e => ({
										source: e.source().id(),
										target: e.target().id()
									}))
								};

								$this.copiedNode = null;
								return;
							}

							$this.copiedNode = e.target.data();
							$this.copiedBlock = null;
						},
						disabled: false,
					},
					{
						id: "Paste",
						content: "Paste Integration",
						tooltipText: "Paste Node",
						image: { src: paste, width: 20, height: 20, x: 6, y: 20 },
						coreAsWell: true,
						show: true,
						onClickFunction: async function (e) {
							const pos = e.renderedPosition;

							if ($this.copiedBlock) {
								const offset = { x: 40, y: 40 };

								const nodesPayload = $this.copiedBlock.nodes.map((node) => ({
									name: node.data.node.name,
									canvasPosition: {
										x: node.position.x + offset.x,
										y: node.position.y + offset.y,
									},
									parameters: node.data.node.parameters,
									outgoingConnections: [],
								}));

								const newIds = await $this.addNewNodesToWorkflow(nodesPayload);

								const idMap = {};
								$this.copiedBlock.nodes.forEach((node, index) => {
									idMap[node.data.id] = newIds[index];
								});

								$this.copiedBlock.edges.forEach((edge) => {
									const source = idMap[edge.source];
									const target = idMap[edge.target];
									if (source && target) {
										cy.add({
											group: "edges",
											data: { source, target },
										});
									}
								});

								$this.updateUnsavedChangesStateMETHOD(true);
								return;
							}

							if (!$this.copiedNode) return;

							$this.disableGridAutoSnap(false);
							await $this.addNewNodeToWorkflow(
								$this.copiedNode.node.name,
								pos,
								$this.copiedNode.node.parameters
							);
							$this.disableGridAutoSnap(true);
						},
						disabled: false,
					},
				],
				menuItemClasses: ["rightClickMenuItem"],
				contextMenuClasses: ["rightClickMenu"],
			};

			const rightClickMenu = cy.contextMenus(contextMenuSettings);
			cy.on("cxttap", (e) => {
				const targetNode = e.target.data().id;
				const node = e.target;
				const nodeData = node?.data()?.node;

				rightClickMenu.showMenuItem("Delete");
				rightClickMenu.showMenuItem("Copy");
				rightClickMenu.showMenuItem("Paste");

				if (nodeData.type === "note") {
					rightClickMenu.showMenuItem("change-note-color");
					rightClickMenu.showMenuItem("Delete-Note");
				} else {
					rightClickMenu.hideMenuItem("change-note-color");
					rightClickMenu.hideMenuItem("Delete-Note");
				}

				if (!nodeData) {
					rightClickMenu.hideMenuItem("Delete");
					rightClickMenu.hideMenuItem("Copy");
					rightClickMenu.hideMenuItem("Paste");
					return;
				}

				if (nodeData.type === "note") {
					rightClickMenu.hideMenuItem("Delete");
					rightClickMenu.hideMenuItem("Copy");
					rightClickMenu.hideMenuItem("Paste");
					return;
				}

				if ($this.workflow.isActive) {
					rightClickMenu.hideMenuItem("Delete");
					rightClickMenu.hideMenuItem("Delete-Note");
				} else {
					if (targetNode) {
						rightClickMenu.showMenuItem("Delete");
					} else {
						rightClickMenu.hideMenuItem("Delete");
					}
				}
				if (!$this.copiedNode && !$this.copiedBlock) {
					rightClickMenu.hideMenuItem("Paste");
				} else {
					rightClickMenu.showMenuItem("Paste");
				}
				if ($this.readOnlyMode || e.target.data().specialType) {
					rightClickMenu.hideMenuItem("Paste");
					rightClickMenu.hideMenuItem("Copy");
					rightClickMenu.hideMenuItem("Delete");
				}
			});
			//End node menus logic
			if (this.execution) {
				this.$nextTick(() => {
					cy.fit(100);
				});
			} else {
				cy.fit();
			}
			cy.maxZoom(10);
			cy.minZoom(0.5);
			cy.zoom({
				level: 2,
			});
			//Additional Canvas
			const overlayLayer = cy.cyCanvas({
				zIndex: 0,
			});
			this.overlayLayer = overlayLayer.getCanvas();
			const overlayCanvas = overlayLayer.getCanvas();
			const ctxOverlay = overlayCanvas.getContext("2d");
			CanvasRenderingContext2D.prototype.roundRect = function (x, y, w, h, r) {
				if (w < 2 * r) r = w / 2;
				if (h < 2 * r) r = h / 2;
				this.beginPath();
				this.moveTo(x + r, y);
				this.arcTo(x + w, y, x + w, y + h, r);
				this.arcTo(x + w, y + h, x, y + h, r);
				this.arcTo(x, y + h, x, y, r);
				this.arcTo(x, y, x + w, y, r);
				this.closePath();
				return this;
			};
			//Render event listener
			cy.on("render cyCanvas.resize", () => {
				overlayLayer.resetTransform(ctxOverlay);
				overlayLayer.clear(ctxOverlay);
				overlayLayer.setTransform(ctxOverlay);
				ctxOverlay.save();
				cy.nodes().forEach((node) => {
					if (node.data().node) {
						const pos = node.position();
						if (node.data().executionIndex) {
							ctxOverlay.font = "8px Helvetica";
							ctxOverlay.fillStyle = "#67C23A";
							ctxOverlay.fillText(node.data().executionIndex, pos.x - 18, pos.y - 12);
						}
						if (node.data().executionRunTimeMs !== null && node.data().executionRunTimeMs !== undefined) {
							ctxOverlay.font = "9px Helvetica";
							ctxOverlay.fillText("🟢", pos.x + 10, pos.y - 12);
							ctxOverlay.font = "6px Helvetica";
							ctxOverlay.fillStyle = "green";
							let errorTitleY = pos.y + 35;
							if (node.data().inputs?.length) {
								errorTitleY = pos.y + 60;
							}
							ctxOverlay.fillText(
								"Execution time: " + node.data().executionRunTimeMs + " ms",
								pos.x - 25,
								errorTitleY
							);
						}
						if (node.data().deployError) {
							//node.css({ borderColor: "red" });
							ctxOverlay.font = "11px Helvetica";
							ctxOverlay.fillStyle = "red";
							ctxOverlay.fillText("⚠️", pos.x + 10, pos.y - 12);
							ctxOverlay.font = "6px Helvetica";
							let errorTitleY = pos.y + 35;
							let errorMessageY = pos.y + 45;
							if (node.data().inputs?.length) {
								errorTitleY = pos.y + 60;
								errorMessageY = pos.y + 70;
							}
							if ($this.readOnlyMode || $this.workflow.isActive) {
								ctxOverlay.fillText(
									"Workflow execution terminated due to error.",
									pos.x - 25,
									errorTitleY
								);
							} else {
								ctxOverlay.fillText("Cannot turn workflow on due to error.", pos.x - 25, errorTitleY);
							}
							if (typeof node.data().deployError === "object") {
								const errorMsgObjectString = JSON.stringify(node.data().deployError);
								ctxOverlay.fillText("Error: " + errorMsgObjectString, pos.x - 25, errorMessageY);
							} else {
								const errorMsg = node.data().deployError.startsWith("Error:")
									? node.data().deployError
									: "Error: " + node.data().deployError;
								ctxOverlay.fillText(errorMsg, pos.x - 25, errorMessageY);
							}
						}
						if (
							node.data().node &&
							node.data()?.node?.type !== "note" &&
							!node.data().specialType
						) {
							ctxOverlay.fillStyle = "#A2A2B5";
							ctxOverlay.font = "6px Helvetica";
							let text =
								node.data().node.description ||
								node.data().node?.nodeDescription?.description;
							ctxOverlay.fillText(text || "", pos.x + 31, pos.y - 7);
						}
						if (!node.data().executionIndex) {
							ctxOverlay.fillStyle = this.primaryColor;
							ctxOverlay.font = "9px Helvetica";
							if (node.data().node.type === "trigger" || node.data().node.type === "webhook") {
								ctxOverlay.fillText("✧˖°", pos.x - 21, pos.y - 12);
							}
							if (node.data().node.type === "action" && !node.data().node.outputs?.length) {
								ctxOverlay.fillText("✦︎", pos.x - 21, pos.y - 12);
							}
						}
						if (node.data().node.type === "action" && !node.data().node.outputs?.length) {
							const mode = node.data().node.nodeSettings.mode || this.workflow.settings.workflowMode;
							ctxOverlay.fillStyle = this.primaryColor;
							ctxOverlay.font = "10px Helvetica";
							ctxOverlay.fillText("⚙", pos.x + 31, pos.y + 14);
							ctxOverlay.font = "6px Helvetica";
							ctxOverlay.fillText(mode, pos.x + 39, pos.y + 13);
						}
					}
				});
				ctxOverlay.restore();
			});
			
			// Initial sync of tour anchors
			$this.syncTourAnchors();
		},
		syncTourAnchors() {
			if (!this.cy) return;
			// Clean up old anchors
			this.tourAnchors.forEach(a => {
				if (a.popper) a.popper.destroy();
				if (a.el && a.el.parentNode) a.el.parentNode.removeChild(a.el);
			});
			this.tourAnchors = [];

			this.cy.nodes().forEach(node => {
				const nodeData = node.data().node;
				if (!nodeData || node.data().specialType) return;

				const name = nodeData.name;
				if (!name) return;

				// Create a predictable ID for the tour to target
				// Normalizes "Respond to Webhook" -> "tour-node-respond-to-webhook"
				const kebabName = name.toLowerCase().replace(/[\s\W]+/g, "-");
				const anchorId = `tour-node-${kebabName}`;

				// Create invisible anchor element
				const el = document.createElement("div");
				el.id = anchorId;
				el.className = "tour-node-anchor";
				el.style.position = "absolute";
				el.style.width = "60px";
				el.style.height = "60px";
				el.style.pointerEvents = "none";
				el.style.visibility = "visible";
				el.style.opacity = "0";
				
				const container = document.querySelector("#workflowCanvas");
				if (container) {
					container.appendChild(el);
				}

				const popperInstance = node.popper({
					content: el,
					popper: {
						placement: "center",
						modifiers: [{ name: "offset", options: { offset: [0, 0] } }]
					}
				});

				this.tourAnchors.push({ id: anchorId, el, popper: popperInstance });
			});
		},
	},
	data() {
		return {
			backgroundPattern: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='7' viewBox='0 0 1 16'%3E%3Cpath fill='%237e7c83' fill-opacity='1' d='M1 3h1v1H1V3zm2-2h1v1H3V1z'%3E%3C/path%3E%3C/svg%3E")`,
			gridSettingEnabled: false,
			wasExecutionCompletionEvent: true,
			chainOfThoughtSseEvents: false,
			loElement: null,
			loadingPopper: null,
			nodeSseEventQueue: [],
			processingAnimationInProgress: false,
			processingNodeSseEvent: false,
			autoMoveRules: [],
			activeNodeId: null,
			dropCoordinates: null,
			nodeSelectionMenuVisible: false,
			nodeSelectionFilter: null,
			nodeContentsVisible: false,
			updatedAt: "",
			executedAt: "",
			copiedNode: null,
			loadingWorkflow: false,
			overlayLayer: null,
			nodes: [],
			tourAnchors: [],
			copiedBlock: null,
			note_colors: [
				"#FFF7CC",
				"#E7F5FF",
				"#ECFDF5",
				"#FCE7F3",
				"#FEF3C7"
			],
			config: {
				cy: null,
				style: [
					{
						selector: "node:selected",
						style: {
							"border-width": 2,
							"border-style": "dashed",
							"border-color": function () {
								return "#ff0000";
							},
						}
					},
					{
						selector: "node",
						style: {
							width: function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "8px";
								}
								if (ele.data()?.node?.type === "note") {
									return "220px";
								}
								return "50px";
							},
							height: function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "8px";
								}
								if (ele.data()?.node?.type === "note") {
									return "140px";
								}
								return "50px";
							},
							color: function (ele) {
								if (ele.data()?.node?.type === "note") {
									return "#000000";
								}
								return "#293953";
							},
							"border-width": function (ele) {
								if (ele.data()?.node?.type === "note") {
									return 0;
								}
								return 0.8;
							},
							"border-color": function (ele) {
								if (ele.data()?.node?.type === "note") {
									return "transparent";
								}
								return "#A2A2B5";
							},
							shape: function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "ellipse";
								}
								if (ele.data()?.node?.type === "note") {
									return "round-rectangle";
								}
								return "round-rectangle";
							},
							"background-color": function (ele) {
								if (ele.data()?.node?.type === "note") {
									return ele.data().node.data?.color || "#FFF7CC";
								}
								return "white";
							},
							"background-fit": "none",
							label: function (ele) {
								if (ele.data()?.node?.type === "note") {
									const text = ele.data().node?.data?.text;
									const lines = text.split("\n");
									if (lines.length <= 5) return text;

									return lines.slice(0, 5).join("\n") + "…";
								}
								if (ele.data()?.specialType === "contextualNodeInput") {
									return ele.data().node?.displayName?.split(" ")[0];
								}
								return (
									ele.data().node?.displayName || ele.data().node?.nodeDescription?.displayName || " "
								);
							},
							"text-margin-y": function (ele) {
								if (ele.data()?.node?.type === "note") {
									return "0px";
								}
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "-4px";
								}
								return "10px";
							},
							"text-margin-x": function (ele) {
								if (ele.data()?.node?.type === "note") {
									return "-4px";
								}
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "0px";
								}
								return "6px";
							},
							"text-halign": function (ele) {
								if (ele.data()?.node?.type === "note") {
									return "center";
								}
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "center";
								}
								return "right";
							},
							"text-valign": function (ele) {
								if (ele.data()?.node?.type === "note") {
									return "center";
								}
								return "inherit"
							},
							"font-size": function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "4px";
								}
								if (ele.data()?.node?.type === "note") {
									return "10px";
								}
								return "8px";
							},
							"font-weight": function (ele) {
								if (ele.data()?.node?.type === "note") {
									return "normal";
								}
								return "bold";
							},
							"font-family": "Helvetica",
							"background-image": function (ele) {
								if (ele.data()?.node?.type === "note") {
									return "none";
								}

								if (ele.data()?.specialType === "contextualNodeInput") {
									return input;
								}
								const mime = ele.data().node?.iconMimeType === "png" ? "png" : "svg+xml";
								return ele.data().node
									? `url(data:image/${mime};base64,${ele.data().node.icon})`
									: "url(data:image/svg+xml;base64";
							},
							"background-height": function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "5px";
								}
								if (ele.data()?.node?.type === "note") {
									return "0px";
								}
								return "35px";
							},
							"background-width": function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "5px";
								}
								if (ele.data()?.node?.type === "note") {
									return "0px";
								}
								return "35px";
							},
							"bounds-expansion": "20px,",
							"background-clip": "none",
						},
					},
					{
						selector: "edge",
						style: {
							"curve-style": "unbundled-bezier",
							"control-point-distances": (edge) => edge.data("distances") || [0, 0],
							"control-point-weights": (edge) => edge.data("weights") || [0.25, 0.75],
							"taxi-turn": function () {
								return "90%";
							},
							"taxi-direction": function () {
								return "horizontal";
							},
							width: 1,
							"line-color": "#A2A2B5",
							"target-arrow-color": "#A2A2B5",
							"target-arrow-shape": function (ele) {
								const targetNode = globalThis.cy.nodes(`#${ele.data().target}`);
								if (targetNode.data().specialType) {
									return "none";
								}
								return "triangle";
							},
							"line-style": function () {
								return "dashed";
							},
							"source-endpoint": function (ele) {
								const targetNode = globalThis.cy.nodes(`#${ele.data().target}`);
								if (targetNode.data().specialType) {
									return "0% 0%";
								}
								if (globalThis.workflow.settings.canvasConnectorStyle === "taxi") {
									return "0% 0%";
								}
								return ele.data("endpoints-source") || "0% 0%";
							},
							"target-endpoint": function (ele) {
								const targetNode = globalThis.cy.nodes(`#${ele.data().target}`);
								if (targetNode.data().specialType) {
									return "0% 0%";
								}
								if (globalThis.workflow.settings.canvasConnectorStyle === "taxi") {
									return "outside-to-node";
								}
								return ele.data("endpoints-target") || "0% 0%";
							},
							/* "source-label": function (ele) {
								const targetNode = globalThis.cy.nodes(`#${ele.data().target}`);
								if (targetNode.data().specialType) {
									return "Used as";
								}
								return "";
							}, */
							/* "source-text-margin-y": function () {
								return "-50%";
							},
							"source-text-margin-x": function () {
								return "0%";
							}, */
							"text-valign": "center", //Center the text vertically
							"text-rotation": "autorotate",
							color: "white",
							"text-background-padding": "1px",
							"font-size": "4px",
							"text-background-color": "#293953",
							"text-background-opacity": "1",
							"text-background-shape": "round-rectangle",
							"text-border-opacity": "1",
							"text-border-width": "6px",
							"text-border-color": "#293953",
						},
					},
				],
			},
		};
	},
};
</script>

<style lang="scss">
@import "../theme/baseStyles/index.scss";

#workflowCanvas {
	cursor: grab;
	position: relative;
	overflow: hidden;
	width: 100%;
	height: 100%;
	background-color: transparent;
	position: relative;
	z-index: 0;
}

#cytoscape-div {
	height: calc(100vh - 124px);
}

canvas {
	left: 0px;
}

.edgeHandle {
	background: $--color-primary;
	border-radius: 100%;
	z-index: 999;
	cursor: pointer;
}

.runWorkflowBtn {
	background: $--color-primary;
	border-radius: 12%;
	z-index: 999;
	cursor: pointer;
}

.executionOngoingIcon {
	border-radius: 100%;
	z-index: 999;
	height: 50px;
	width: 50px;
}

.deleteEdge {
	border-radius: 100%;
	cursor: pointer;
	z-index: 999;
	background-color: $--color-danger;
}

.rightClickMenu {
	border-radius: 4px;
	border: 1px solid $--color-primary;
	background-color: white;
	box-shadow: 0 1px 15px rgb(0 0 0 / 4%);
}

.rightClickMenuItem {
	padding: 20px;
	font-size: 18px;
	background-color: white;
	border-bottom: 1px solid $--color-primary;
	color: #2c3e50 !important;
	padding-left: 35px;
	border-radius: 4px;
}

.rightClickMenuItem:hover img {
	filter: invert(100%);
}

.rightClickMenuItem:hover {
	background-color: $--color-primary;
	color: white !important;
}

.rightClickMenuItem:first-child {
	border-top-left-radius: 4px;
	border-top-right-radius: 4px;
}

.rightClickMenuItem:last-child {
	border-bottom: 0px !important;
	border-bottom-left-radius: 4px;
	border-bottom-right-radius: 4px;
}

.popperIconWrapper {
	position: relative;
}

.popperIcon {
	position: absolute;
	pointer-events: none;
	height: 0px;
}

.loaderIcon {
	position: absolute;
	pointer-events: none;
	height: 50px;
	width: 50px;
}

.status {
	position: absolute;
	z-index: 999;
	bottom: 20px;
	left: 30px;
	display: inline-flex;
	align-items: center;
	pointer-events: none;
	user-select: none;
}

.status i {
	font-size: 25px;
	margin-right: 5px;
}

.controlBar {
	position: absolute;
	right: 40px;
	bottom: 40px;
	display: inline-flex;
	z-index: 10;
}

.arrowType i {
	font-weight: 900 !important;
}

.workflowStateMessage {
	position: absolute;
	opacity: 0.8;
	z-index: 10;
}

.workflowErrorMessage,
.workflowWarningMessage {
	position: absolute !important;
	top: 41px;
	opacity: 0.8 !important;
	z-index: 10;
}

.progressWrapper {
	position: absolute;
	width: 300px;
	left: 0;
	right: 0;
	margin: auto;
	top: 50px;
	z-index: 999;
}

.progressWrapper h4 {
	text-align: center;
}

.progress {
	padding: 6px;
	background: rgba(0, 0, 0, 0.05);
	border-radius: 6px;
	box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.25), 0 1px rgba(255, 255, 255, 0.08);
}

.progress-bar {
	height: 18px;
	border-radius: 4px;
	transition: 0.4s linear;
	transition-property: width, background-color;
}

.progress-infinite .progress-bar {
	width: 100%;
	animation: colorAnimation 1s infinite;
}

@keyframes colorAnimation {
	0% {
		background: #a28089;
	}

	25% {
		background: #a0d2eb;
	}

	50% {
		background: #ffa8b6;
	}

	75% {
		background: #d0bdf4;
	}

	100% {
		background: #494d5f;
	}
}

.progressWrapper .cancelWorkflowAnimation {
	margin: auto;
	display: block;
	margin-top: 5px;
}

.note-editor {
	position: absolute;
	min-width: 200px;
	min-height: 100px;
	resize: none;
	border-radius: 8px;
	padding: 8px;
	font-size: 13px;
	outline: none;
	border: 1px solid #ccc;
	z-index: 9999;
}

.note-label {
	white-space: pre-wrap;
	word-break: break-word;
	overflow-wrap: anywhere;
	max-width: 220px;
	max-height: 120px;
	overflow-y: auto;
	overflow-x: hidden;
}

.note-content {
	max-height: 120px;
	overflow-y: auto;
	overflow-x: hidden;
	white-space: pre-wrap;
	word-break: break-word;
}

#stickyNotebtn {
	color: #000000;
}

#stickyNotebtn:hover {
	color: #ffffff;
}

#stickyNotebtn.focusing:hover {
	color: #ffffff;
}

#stickyNotebtn:focus {
	color: #ffffff;
}
</style>
